import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';

const Gallery = ({ user, onLogout }) => {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, image, video, audio
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    file: null,
    caption: ''
  });

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({ ...uploadData, file });
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadData.file);
    if (uploadData.caption) {
      formData.append('caption', uploadData.caption);
    }

    try {
      await axios.post(`${API}/gallery/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded successfully!');
      setShowUploadModal(false);
      setUploadData({ file: null, caption: '' });
      fetchGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axios.delete(`${API}/gallery/${itemId}`);
      toast.success('Item deleted');
      fetchGallery();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const filteredItems = items.filter(item => 
    filter === 'all' || item.file_type === filter
  );

  const getFileIcon = (fileType) => {
    switch(fileType) {
      case 'image': return '🖼️';
      case 'video': return '🎬';
      case 'audio': return '🎵';
      default: return '📁';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="dashboard-container" data-testid="gallery-page">
      <NavBar onLogout={onLogout} />

      <div className="dashboard-content page-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1 className="page-title" data-testid="page-title">Media Gallery</h1>
          <button 
            className="submit-button"
            style={{ width: 'auto', padding: '0.75rem 1.5rem' }}
            onClick={() => setShowUploadModal(true)}
            data-testid="upload-button"
          >
            📤 Upload Media
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="gallery-filters" style={{ marginBottom: '2rem' }}>
          <button 
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
            data-testid="filter-all"
          >
            All ({items.length})
          </button>
          <button 
            className={`filter-tab ${filter === 'image' ? 'active' : ''}`}
            onClick={() => setFilter('image')}
            data-testid="filter-images"
          >
            🖼️ Photos ({items.filter(i => i.file_type === 'image').length})
          </button>
          <button 
            className={`filter-tab ${filter === 'video' ? 'active' : ''}`}
            onClick={() => setFilter('video')}
            data-testid="filter-videos"
          >
            🎬 Videos ({items.filter(i => i.file_type === 'video').length})
          </button>
          <button 
            className={`filter-tab ${filter === 'audio' ? 'active' : ''}`}
            onClick={() => setFilter('audio')}
            data-testid="filter-audio"
          >
            🎵 Audio ({items.filter(i => i.file_type === 'audio').length})
          </button>
        </div>

        {/* Gallery Grid */}
        <div className="section-card">
          {loading ? (
            <p>Loading gallery...</p>
          ) : filteredItems.length === 0 ? (
            <div className="empty-state" data-testid="empty-gallery">
              <div className="empty-state-icon">📷</div>
              <p>No media files yet</p>
              <p style={{ fontSize: '0.9rem', color: '#6b8899' }}>
                Upload photos, videos, or audio files to build your gallery
              </p>
            </div>
          ) : (
            <div className="gallery-grid">
              {filteredItems.map((item) => (
                <div key={item.id} className="gallery-item-card" data-testid={`gallery-item-${item.id}`}>
                  <div className="gallery-item-preview">
                    {item.file_type === 'image' ? (
                      <img 
                        src={`data:${item.mime_type};base64,${item.file_data}`}
                        alt={item.caption || item.file_name}
                        className="gallery-image"
                      />
                    ) : (
                      <div className="gallery-placeholder">
                        <span style={{ fontSize: '3rem' }}>{getFileIcon(item.file_type)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="gallery-item-info">
                    <h4>{item.file_name}</h4>
                    {item.caption && <p className="gallery-caption">{item.caption}</p>}
                    <div className="gallery-meta">
                      <span>{new Date(item.uploaded_at).toLocaleDateString()}</span>
                      <span>{formatFileSize(item.file_size)}</span>
                    </div>
                    
                    <div className="gallery-actions">
                      {item.file_type === 'video' && (
                        <a 
                          href={`data:${item.mime_type};base64,${item.file_data}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gallery-action-btn"
                        >
                          ▶️ Play
                        </a>
                      )}
                      {item.file_type === 'audio' && (
                        <audio controls style={{ width: '100%', marginBottom: '0.5rem' }}>
                          <source src={`data:${item.mime_type};base64,${item.file_data}`} type={item.mime_type} />
                        </audio>
                      )}
                      <button 
                        className="delete-button"
                        onClick={() => handleDelete(item.id)}
                        data-testid={`delete-${item.id}`}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" data-testid="upload-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Upload Media</h2>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}>
                ✕
              </button>
            </div>

            <div className="upload-form">
              <div className="form-group">
                <label htmlFor="file">Select File</label>
                <input
                  id="file"
                  type="file"
                  accept="image/*,video/*,audio/*"
                  onChange={handleFileSelect}
                  data-testid="file-input"
                />
                {uploadData.file && (
                  <p style={{ marginTop: '0.5rem', color: '#5cb85c' }}>
                    ✓ {uploadData.file.name}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="caption">Caption (Optional)</label>
                <textarea
                  id="caption"
                  rows="2"
                  value={uploadData.caption}
                  onChange={(e) => setUploadData({ ...uploadData, caption: e.target.value })}
                  placeholder="Add a description..."
                  data-testid="caption-input"
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  className="submit-button"
                  onClick={handleUpload}
                  disabled={uploading || !uploadData.file}
                  data-testid="submit-upload"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
                <button
                  className="submit-button"
                  onClick={() => setShowUploadModal(false)}
                  style={{ background: '#6c757d' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
